import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { IconChannelsOrder, IconDelete, IconShare } from '../../../../../assets/popup-nav-icons';
import { useBackdropContext } from '../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../context/installation';
import {
  DashboardItemType,
  RemoveGroupMutation,
  RemoveGroupMutationVariables,
  UpdateGroupMutation,
  UpdateGroupMutationVariables,
  UserGroup,
  UserPermissionType,
} from '../../../../../data-access/gql-types/graphql';
import { UPDATE_GROUP } from '../../../../../data-access/mutations/groups';
import { REMOVE_GROUP } from '../../../../../data-access/mutations/groups';
import { useMutationErrors } from '../../../../../hooks/errors/use-mutation-errors';
import { useRefetchData } from '../../../../../hooks/refresh-data/use-refetch-data';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { useShareRedirect } from '../../../../../modules/installation-share/hooks/use-share-redirect';
import { ROUTES } from '../../../../../routes/routes';
import { MutationErrorType } from '../../../../../types';
import { toastError, toastSuccess } from '../../../../../utils/toast';
import { useGroups } from '../../../hooks/groups/use-groups';
import * as storage from '../../../storage';
import { useFormChannelsContext } from '../context';

export const useEditGroup = () => {
  const { t } = useTranslation('groups');
  const { t: tc } = useTranslation('common');
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const { groups } = useGroups();
  const [userGroup, setGroup] = useState<UserGroup>();
  const { isPristine, prepareGroupItems } = useFormChannelsContext();
  const [defaultChannels, setDefaultChannels] = useState<string[]>([]);
  const [defaultActions, setDefaultActions] = useState<string[]>([]);
  const { handleErrors } = useMutationErrors(MutationErrorType.Group);
  const { selectedInstallationId } = useInstallationContext();
  const { shareGroup } = useShareRedirect();
  const { refetchGroups } = useRefetchData();
  const { permissions } = useUserPermissions();
  const [updateGroup, { loading: editGroupNameLoading }] = useMutation<
    UpdateGroupMutation,
    UpdateGroupMutationVariables
  >(UPDATE_GROUP);
  const form = useForm({
    defaultValues: {
      name: '',
      iconName: '',
    },
  });
  const [removeGroup] = useMutation<RemoveGroupMutation, RemoveGroupMutationVariables>(REMOVE_GROUP);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();

  const canShare = useMemo(
    () => permissions[UserPermissionType.GroupShare] && userGroup?.group.isPublicGroup,
    [userGroup?.group.isPublicGroup, permissions],
  );

  useEffect(() => {
    if (groupId) setGroup(groups?.find((group) => group.id === groupId));
  }, [groups, groupId]);

  useEffect(() => {
    if (userGroup && !defaultChannels.length) {
      setDefaultChannels(
        userGroup.group.groupItems
          ?.filter((item) => item.id && item.resourceType === DashboardItemType.Channel)
          .map((item) => item.id) || [],
      );
    }

    if (userGroup && !defaultActions?.length) {
      setDefaultActions(
        userGroup.group.groupItems
          ?.filter((item) => item.id && item.resourceType === DashboardItemType.Action)
          .map((item) => item.id) || [],
      );
    }

    if (userGroup) {
      form.setValue('name', userGroup.group.name);
      form.setValue('iconName', userGroup.group.iconName);
    }
  }, [userGroup]);

  const openRemoveDialog = () => setShowDeleteDialog(true);
  const closeRemoveDialog = () => setShowDeleteDialog(false);
  const handleAttemptToSave = () => setShowSaveDialog(true);

  const handleChangeIcon = (value: string) => form.setValue('iconName', value);

  const handleRemoveGroup = () => {
    if (userGroup) {
      turnOnBackdrop();
      closeRemoveDialog();
      removeGroup({
        variables: {
          input: {
            installationId: selectedInstallationId,
            groupId: userGroup?.id,
          },
        },
        onCompleted: (data) => {
          turnOffBackdrop();
          if (!data || !data.removeGroup) {
            toastError({ content: tc('errors.somethingWentWrong') });
            return;
          }

          if (data.removeGroup.result?.ok) {
            refetchGroups();

            toastSuccess({ content: t('groupDeleted') });
            navigate(-1);
          } else {
            handleErrors(data.removeGroup.errors || []);
          }
        },
        onError: () => {
          turnOffBackdrop();
          toastError({ content: `${t('removeError.default')}` });
        },
      });
    }
  };

  const onCancel = () => {
    setShowSaveDialog(false);
    setShowDeleteDialog(false);
  };

  const handleRedirectionToGroupSortChannels = () => {
    const isListView = storage.getItem('isListView');
    storage.setItem('lastActiveGroupId', String(userGroup?.id));

    navigate(ROUTES.GroupSortChannels(isListView));
  };

  const confirmSubmit = () => {
    const { name, iconName } = form.getValues();
    const groupItems = prepareGroupItems();

    if (userGroup) {
      turnOnBackdrop();
      updateGroup({
        variables: {
          input: {
            installationId: selectedInstallationId,
            groupId: userGroup.id,
            name,
            groupItems,
            iconName,
          },
        },
        onCompleted: (data) => {
          turnOffBackdrop();
          if (!data || !data.updateGroup) {
            toastError({ content: tc('errors.somethingWentWrong') });
            return;
          }

          if (data.updateGroup.result?.ok) {
            refetchGroups();
            setShowSaveDialog(false);
            toastSuccess({ content: t('groupEdited') });
            navigate(-1);
          } else {
            handleErrors(data.updateGroup.errors || []);
          }
        },
        onError: () => turnOffBackdrop(),
      });
    } else {
      toastError({ content: tc('errors.somethingWentWrong') });
    }
  };

  const onSubmit = form.handleSubmit(() => handleAttemptToSave());

  const canRemoveGroup = useMemo(() => {
    if (!groupId) return false;
    return userGroup?.group.isPublicGroup
      ? permissions[UserPermissionType.PublicGroupRemove]
      : permissions[UserPermissionType.GroupRemove];
  }, [permissions, groupId, userGroup]);

  const popupNavLinks = useMemo(() => {
    return [
      { label: t('channelsOrder'), onClick: handleRedirectionToGroupSortChannels, icon: <IconChannelsOrder /> },
      ...(canRemoveGroup
        ? [{ label: t('remove'), onClick: openRemoveDialog, hasLineBelow: canShare, icon: <IconDelete /> }]
        : []),
      ...(canShare
        ? [
            {
              label: t('shareGroup'),
              onClick: () => shareGroup(userGroup),
              icon: <IconShare />,
            },
          ]
        : []),
    ];
  }, [groupId, canShare, canRemoveGroup, handleRedirectionToGroupSortChannels, openRemoveDialog, shareGroup]);

  const isfavouriteForm = useMemo(() => pathname === ROUTES.FavouriteGroupEdit(groupId), [pathname, groupId]);

  return {
    showDeleteDialog,
    showSaveDialog,
    userGroup,
    form,
    editGroupNameLoading,
    isPristine,
    defaultChannels,
    defaultActions,
    popupNavLinks,
    isfavouriteForm,
    setShowDeleteDialog,
    setShowSaveDialog,
    handleChangeIcon,
    onSubmit,
    handleRemoveGroup,
    handleAttemptToSave,
    confirmSubmit,
    closeRemoveDialog,
    onCancel,
  };
};
